import { Badge, Button, confirm, Input, Modal } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  Info,
  Link2,
  Plus,
  Redo2,
  Trash2,
  Undo2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Background,
  Controls,
  type Edge,
  MiniMap,
  type Node,
  type NodeMouseHandler,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from "reactflow";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import "reactflow/dist/style.css";
import {
  useCreateFlowConnection,
  useCreateFlowStep,
  useDeleteFlowConnection,
  useDeleteFlowStep,
  useFlowQuery,
  useUpdateFlowConnection,
  useUpdateFlowStep,
} from "../api/queries";
import { FlowStepForm } from "../ui/FlowStepForm";
import { FlowStepNode } from "../ui/FlowStepNode";
import { FlowStepTypeManagementModal } from "../ui/FlowStepTypeManagementModal";

// Define node types for React Flow
const nodeTypes = {
  flowStep: FlowStepNode,
};

// History state for Undo/Redo
interface HistoryState {
  nodes: Node[];
  edges: Edge[];
}

// Helper function to determine best handle positions based on node positions
function getHandlePositions(
  sourceNode: Node | undefined,
  targetNode: Node | undefined,
): { sourceHandle: string; targetHandle: string } {
  if (!sourceNode || !targetNode) {
    return { sourceHandle: "right", targetHandle: "left" };
  }

  const sourceX = sourceNode.position.x;
  const sourceY = sourceNode.position.y;
  const targetX = targetNode.position.x;
  const targetY = targetNode.position.y;

  // Calculate differences
  const deltaX = Math.abs(targetX - sourceX);
  const deltaY = Math.abs(targetY - sourceY);

  // If vertical alignment is stronger (more vertical than horizontal)
  if (deltaY > deltaX) {
    if (targetY > sourceY) {
      // Target is below source
      return { sourceHandle: "bottom", targetHandle: "top" };
    }
    // Target is above source
    return { sourceHandle: "top", targetHandle: "bottom" };
  }
  // Horizontal alignment
  if (targetX > sourceX) {
    // Target is to the right of source
    return { sourceHandle: "right", targetHandle: "left" };
  }
  // Target is to the left of source
  return { sourceHandle: "left", targetHandle: "right" };
}

export function FlowEditorPage() {
  const { id } = useParams({ from: "/app/flows/$id/edit" });
  const nav = useNavigate();
  const flow = useFlowQuery(id, true, true);
  const createStepModal = useDisclosure();
  const editStepModal = useDisclosure();
  const createConnectionModal = useDisclosure();
  const stepTypeManagementModal = useDisclosure();
  const editConnectionModal = useDisclosure<{
    id: string;
    fromStepId: string;
    toStepId: string;
    label: string | null;
  }>();
  const instructionsModal = useDisclosure();
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);

  // Connection form state
  const [connectionForm, setConnectionForm] = useState({
    fromStepId: "",
    toStepId: "",
    label: "",
  });

  const createFlowStep = useCreateFlowStep(id);
  const updateFlowStep = useUpdateFlowStep(id);
  const deleteFlowStep = useDeleteFlowStep(id);
  const createFlowConnection = useCreateFlowConnection(id);
  const updateFlowConnection = useUpdateFlowConnection(id);
  const deleteFlowConnection = useDeleteFlowConnection(id);

  // History for Undo/Redo
  const [history, setHistory] = useState<HistoryState[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const historyRef = useRef<{ nodes: Node[]; edges: Edge[] } | null>(null);

  // Convert flow steps to ReactFlow nodes
  const initialNodes = useMemo<Node[]>(() => {
    if (!flow.data?.steps || flow.data.steps.length === 0) {
      return [];
    }

    return flow.data.steps.map(
      (step: {
        id: string;
        stepNumber: number;
        positionX: number;
        positionY: number;
        title: string;
        description: string | null;
        stepTypeName: string | null;
        positionName: string | null;
        departmentName: string | null;
      }) => {
        // Use stored position if available, otherwise auto-layout horizontally
        const x = step.positionX !== 0 ? step.positionX : step.stepNumber * 250;
        const y = step.positionY !== 0 ? step.positionY : 100;

        return {
          id: step.id,
          type: "flowStep",
          position: { x, y },
          data: { step },
          draggable: true,
        };
      },
    );
  }, [flow.data?.steps]);

  // Convert flow connections to ReactFlow edges
  const initialEdges = useMemo<Edge[]>(() => {
    if (!flow.data?.connections || flow.data.connections.length === 0) {
      return [];
    }

    return flow.data.connections.map(
      (connection: {
        id: string;
        fromStepId: string;
        toStepId: string;
        label: string | null;
      }) => {
        const sourceNode = initialNodes.find(
          (n) => n.id === connection.fromStepId,
        );
        const targetNode = initialNodes.find(
          (n) => n.id === connection.toStepId,
        );
        const handles = getHandlePositions(sourceNode, targetNode);

        return {
          id: connection.id,
          source: connection.fromStepId,
          target: connection.toStepId,
          sourceHandle: handles.sourceHandle,
          targetHandle: handles.targetHandle,
          label: connection.label || undefined,
          type: "smoothstep",
          animated: false,
          style: {
            stroke: "#3b82f6",
            strokeWidth: 3,
          },
        };
      },
    );
  }, [flow.data?.connections, initialNodes]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update nodes and edges when data changes
  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes, setNodes]);

  useEffect(() => {
    setEdges(initialEdges);
  }, [initialEdges, setEdges]);

  // Save current state to history
  const saveToHistory = useCallback(
    (nodes: Node[], edges: Edge[]) => {
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push({
        nodes: JSON.parse(JSON.stringify(nodes)),
        edges: JSON.parse(JSON.stringify(edges)),
      });
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
      // Limit history to 50 states
      if (newHistory.length > 50) {
        setHistory(newHistory.slice(-50));
        setHistoryIndex(49);
      }
    },
    [history, historyIndex],
  );

  // Undo
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const prevState = history[historyIndex - 1];
      if (prevState) {
        setNodes(prevState.nodes);
        setEdges(prevState.edges);
        setHistoryIndex(historyIndex - 1);
      }
    }
  }, [history, historyIndex, setNodes, setEdges]);

  // Redo
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const nextState = history[historyIndex + 1];
      if (nextState) {
        setNodes(nextState.nodes);
        setEdges(nextState.edges);
        setHistoryIndex(historyIndex + 1);
      }
    }
  }, [history, historyIndex, setNodes, setEdges]);

  // Handle node drag end - auto-save position
  const onNodeDragStop = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      const step = node.data?.step;
      if (step && node.position) {
        updateFlowStep.mutate({
          stepId: node.id,
          data: {
            positionX: Math.round(node.position.x),
            positionY: Math.round(node.position.y),
          },
        });
      }
    },
    [updateFlowStep],
  );

  // Handle connection creation - disabled drag and drop
  const onConnect = useCallback(() => {
    // Disabled - using modal instead
  }, []);

  // Handle node click - select step
  const onNodeClick: NodeMouseHandler = useCallback((_event, node) => {
    setSelectedStepId(node.id);
  }, []);

  // Handle node double click - open edit modal
  const onNodeDoubleClick: NodeMouseHandler = useCallback(
    (_event, node) => {
      setSelectedStepId(node.id);
      editStepModal.open();
    },
    [editStepModal],
  );

  // Handle edge click - select connection
  const onEdgeClick = useCallback((_event: React.MouseEvent, edge: Edge) => {
    setSelectedConnectionId(edge.id);
  }, []);

  // Handle edge double click - edit connection
  const onEdgeDoubleClick = useCallback(
    (_event: React.MouseEvent, edge: Edge) => {
      setSelectedConnectionId(edge.id);
      const connection = flow.data?.connections?.find(
        (c: { id: string }) => c.id === edge.id,
      );
      if (connection) {
        editConnectionModal.openWith({
          id: edge.id,
          fromStepId: edge.source || "",
          toStepId: edge.target || "",
          label: typeof edge.label === "string" ? edge.label : null,
        });
      }
    },
    [editConnectionModal, flow.data?.connections],
  );

  // Handle edge delete - delete individual connections
  const onEdgesDelete = useCallback(
    (deletedEdges: Edge[]) => {
      deletedEdges.forEach((edge) => {
        if (edge.id && !edge.id.startsWith("temp-")) {
          deleteFlowConnection.mutate(edge.id);
        }
      });
      setEdges((prevEdges) =>
        prevEdges.filter((e) => !deletedEdges.find((de) => de.id === e.id)),
      );
    },
    [deleteFlowConnection, setEdges],
  );

  // Handle node delete
  const handleDeleteStep = useCallback(async () => {
    if (!selectedStepId) return;

    const step = nodes.find((n) => n.id === selectedStepId)?.data?.step;
    if (!step) return;

    if (
      await confirm({
        title: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບຂັ້ນຕອນ "${step.title}"?`,
        description: "ຂັ້ນຕອນນີ້ຈະຖືກລຶບອອກຈາກ Flow",
        actionText: "ລຶບ",
        cancelText: "ຍົກເລີກ",
        ActionProps: {
          variant: "destructive",
        },
      })
    ) {
      await deleteFlowStep.mutateAsync(selectedStepId);
      setNodes(nodes.filter((n) => n.id !== selectedStepId));
      setEdges(
        edges.filter(
          (e) => e.source !== selectedStepId && e.target !== selectedStepId,
        ),
      );
      setSelectedStepId(null);
      editStepModal.close();
    }
  }, [
    selectedStepId,
    nodes,
    edges,
    deleteFlowStep,
    setNodes,
    setEdges,
    editStepModal,
  ]);

  // Get selected step data
  const selectedStep = useMemo(() => {
    if (!selectedStepId) return null;
    const node = nodes.find((n) => n.id === selectedStepId);
    return node?.data?.step || null;
  }, [selectedStepId, nodes]);

  // Auto-save debounced
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (historyRef.current) {
        saveToHistory(historyRef.current.nodes, historyRef.current.edges);
        historyRef.current = null;
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [nodes, edges, saveToHistory]);

  // Track changes for history
  useEffect(() => {
    historyRef.current = { nodes, edges };
  }, [nodes, edges]);

  if (flow.isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex h-64 items-center justify-center">
            <div className="text-muted-foreground">ກຳລັງໂຫຼດ...</div>
          </div>
        </Main>
      </>
    );
  }

  if (!flow.data) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex h-64 items-center justify-center">
            <div className="text-muted-foreground">ບໍ່ພົບ Flow</div>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main className="p-0">
        <div className="flex h-[calc(100vh-4rem)]">
          {/* Left Sidebar - Tools */}
          <div className="flex w-80 flex-col border-r bg-card">
            <div className="space-y-2 border-b p-4">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => nav({ to: "/app/flows/$id", params: { id } })}
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <h1 className="flex-1 font-semibold text-lg">ແກ້ໄຂ Flow</h1>
              </div>
              <p className="text-muted-foreground text-sm">{flow.data.title}</p>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto p-4">
              {/* Tools Section */}
              <div className="space-y-2">
                <h3 className="font-medium text-sm">ເຄື່ອງມື</h3>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    onClick={createStepModal.open}
                    size="sm"
                    className="w-full"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    ເພີ່ມຂັ້ນຕອນ
                  </Button>
                  <Button
                    onClick={handleUndo}
                    size="sm"
                    variant="outline"
                    disabled={historyIndex <= 0}
                    className="w-full"
                  >
                    <Undo2 className="mr-2 h-4 w-4" />
                    ຍົກເລີກ
                  </Button>
                  <Button
                    onClick={handleRedo}
                    size="sm"
                    variant="outline"
                    disabled={historyIndex >= history.length - 1}
                    className="w-full"
                  >
                    <Redo2 className="mr-2 h-4 w-4" />
                    ເຮັດຊ້ຳ
                  </Button>
                  <Button
                    onClick={createConnectionModal.open}
                    size="sm"
                    variant="outline"
                    className="w-full"
                  >
                    <Link2 className="mr-2 h-4 w-4" />
                    ເພີ່ມການເຊື່ອມຕໍ່
                  </Button>
                  {selectedStepId && (
                    <Button
                      onClick={handleDeleteStep}
                      size="sm"
                      variant="destructive"
                      className="w-full"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      ລຶບຂັ້ນຕອນ
                    </Button>
                  )}
                </div>
              </div>

              {/* Flow Steps List */}
              <div className="space-y-2 border-t pt-4">
                <h3 className="font-medium text-sm">ລາຍການຂັ້ນຕອນ</h3>
                {flow.data?.steps && flow.data.steps.length > 0 ? (
                  <div className="space-y-2">
                    {flow.data.steps
                      .sort(
                        (
                          a: { stepNumber: number },
                          b: { stepNumber: number },
                        ) => a.stepNumber - b.stepNumber,
                      )
                      .map(
                        (step: {
                          id: string;
                          stepNumber: number;
                          title: string;
                          stepTypeName: string | null;
                          positionName: string | null;
                          notes: string | null;
                        }) => (
                          <button
                            key={step.id}
                            type="button"
                            onClick={() => {
                              setSelectedStepId(step.id);
                            }}
                            onDoubleClick={() => {
                              setSelectedStepId(step.id);
                              editStepModal.open();
                            }}
                            className={`w-full rounded-lg border p-2 text-left transition-colors ${
                              selectedStepId === step.id
                                ? "border-primary bg-primary/10"
                                : "bg-card hover:bg-accent"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {step.stepNumber}
                              </Badge>
                              <span className="flex-1 truncate font-medium text-sm">
                                {step.title}
                              </span>
                            </div>
                            {step.stepTypeName && (
                              <div className="mt-1">
                                <Badge variant="secondary" className="text-xs">
                                  {step.stepTypeName}
                                </Badge>
                              </div>
                            )}
                            {step.positionName && (
                              <div className="mt-1 truncate text-muted-foreground text-xs">
                                {step.positionName}
                              </div>
                            )}
                            {step.notes && (
                              <div className="mt-1 truncate text-muted-foreground text-xs">
                                {step.notes}
                              </div>
                            )}
                          </button>
                        ),
                      )}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-xs">ຍັງບໍ່ມີຂັ້ນຕອນ</p>
                )}
              </div>

              {/* Selected Step Details */}
              {selectedStep && (
                <div className="space-y-2 border-t pt-4">
                  <h3 className="font-medium text-sm">ລາຍລະອຽດຂັ້ນຕອນ</h3>
                  <div className="space-y-1 text-sm">
                    <div>
                      <span className="font-medium">ຊື່:</span>{" "}
                      {selectedStep.title}
                    </div>
                    {selectedStep.description && (
                      <div>
                        <span className="font-medium">ຄຳອະທິບາຍ:</span>{" "}
                        {selectedStep.description}
                      </div>
                    )}
                    {selectedStep.positionName && (
                      <div>
                        <span className="font-medium">ຜູ້ຮັບຜິດຊອບ:</span>{" "}
                        {String(selectedStep.positionName)}
                      </div>
                    )}
                    {selectedStep.departmentName && (
                      <div>
                        <span className="font-medium">ພະແນກ:</span>{" "}
                        {String(selectedStep.departmentName)}
                      </div>
                    )}
                    {selectedStep.stepTypeName && (
                      <div>
                        <span className="font-medium">ປະເພດ:</span>{" "}
                        {selectedStep.stepTypeName}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Instructions */}
              <div className="space-y-2 border-t pt-4 text-muted-foreground text-xs">
                <div className="flex items-center gap-2">
                  <p className="font-medium">ຄຳແນະນຳ:</p>
                  <button
                    type="button"
                    onClick={instructionsModal.open}
                    className="inline-flex items-center justify-center rounded-full transition-colors hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <Info className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                </div>
                <ul className="list-inside list-disc space-y-1">
                  <li>ລາກຂັ້ນຕອນເພື່ອຍ້າຍຕຳແໜ່ງ</li>
                  <li>ກົດທີ່ຂັ້ນຕອນເພື່ອເລືອກ</li>
                  <li>ກົດສອງຄັ້ງທີ່ຂັ້ນຕອນເພື່ອແກ້ໄຂ</li>
                  <li>ກົດສອງຄັ້ງທີ່ການເຊື່ອມຕໍ່ເພື່ອແກ້ໄຂ</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Center - Flow Canvas */}
          <div className="relative flex-1">
            {nodes.length > 0 ? (
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodeClick={onNodeClick}
                onNodeDoubleClick={onNodeDoubleClick}
                onEdgeClick={onEdgeClick}
                onEdgeDoubleClick={onEdgeDoubleClick}
                onNodeDragStop={onNodeDragStop}
                onConnect={onConnect}
                onEdgesDelete={onEdgesDelete}
                nodesConnectable={false}
                nodeTypes={nodeTypes}
                fitView
                className="bg-background"
                connectionLineStyle={{ stroke: "#3b82f6", strokeWidth: 3 }}
                defaultEdgeOptions={{
                  type: "smoothstep",
                  style: { stroke: "#3b82f6", strokeWidth: 3 },
                }}
              >
                <Controls className="border bg-background" />
                <Background color="hsl(var(--muted))" gap={20} />
                <MiniMap
                  className="border bg-background"
                  nodeColor={(node) => {
                    const stepType = node.data?.step?.stepTypeName;
                    if (
                      stepType?.includes("ວຽກ") ||
                      stepType?.includes("work")
                    ) {
                      return "hsl(217, 91%, 60%)";
                    }
                    if (
                      stepType?.includes("ກວດສອບ") ||
                      stepType?.includes("check")
                    ) {
                      return "hsl(43, 96%, 56%)";
                    }
                    if (
                      stepType?.includes("ອະນຸມັດ") ||
                      stepType?.includes("approve")
                    ) {
                      return "hsl(142, 76%, 36%)";
                    }
                    return "hsl(var(--muted-foreground))";
                  }}
                />
              </ReactFlow>
            ) : (
              <div className="flex h-full items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <p className="mb-2">ບໍ່ມີຂັ້ນຕອນ</p>
                  <p className="mb-4 text-sm">ກົດປຸ່ມ "ເພີ່ມຂັ້ນຕອນ" ເພື່ອເລີ່ມຕົ້ນ</p>
                  <Button onClick={createStepModal.open}>
                    <Plus className="mr-2 h-4 w-4" />
                    ເພີ່ມຂັ້ນຕອນ
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Create Step Modal */}
        <Modal
          open={createStepModal.isOpen}
          onOpenChange={createStepModal.toggle}
          title="ເພີ່ມຂັ້ນຕອນໃໝ່"
          size="md"
        >
          <div className="max-h-[calc(100vh-12rem)] overflow-y-auto">
            <FlowStepForm
              flowDepartmentId={flow.data?.departmentId ?? null}
              existingStepsCount={nodes.length}
              onManageStepTypes={stepTypeManagementModal.open}
              onSubmit={async (vals) => {
                await createFlowStep.mutateAsync({
                  stepNumber: vals.stepNumber,
                  title: vals.title,
                  description: vals.description ?? undefined,
                  responsiblePositionId:
                    vals.responsiblePositionId ?? undefined,
                  departmentId: flow.data?.departmentId ?? undefined,
                  stepTypeId: vals.stepTypeId ?? undefined,
                  notes: vals.notes ?? undefined,
                  positionX: nodes.length * 250,
                  positionY: 100,
                });
                // Flow query will refresh automatically via invalidation
                createStepModal.close();
              }}
              submitting={createFlowStep.isPending}
            />
          </div>
        </Modal>

        {/* Edit Step Modal */}
        {selectedStep && (
          <Modal
            open={editStepModal.isOpen}
            onOpenChange={editStepModal.toggle}
            title="ແກ້ໄຂຂັ້ນຕອນ"
            size="md"
          >
            <div className="max-h-[calc(100vh-12rem)] overflow-y-auto">
              <FlowStepForm
                flowDepartmentId={flow.data?.departmentId ?? null}
                existingStepsCount={nodes.length}
                onManageStepTypes={stepTypeManagementModal.open}
                initialValues={{
                  stepNumber: selectedStep.stepNumber,
                  title: selectedStep.title,
                  description: selectedStep.description ?? null,
                  responsiblePositionId:
                    selectedStep.responsiblePositionId ?? null,
                  departmentId: selectedStep.departmentId ?? null,
                  stepTypeId: selectedStep.stepTypeId ?? null,
                  notes: selectedStep.notes ?? null,
                }}
                onSubmit={async (vals) => {
                  if (!selectedStepId) return;
                  await updateFlowStep.mutateAsync({
                    stepId: selectedStepId,
                    data: {
                      stepNumber: vals.stepNumber,
                      title: vals.title,
                      description: vals.description ?? undefined,
                      responsiblePositionId:
                        vals.responsiblePositionId ?? undefined,
                      departmentId: flow.data?.departmentId ?? undefined,
                      stepTypeId: vals.stepTypeId ?? undefined,
                      notes: vals.notes ?? undefined,
                    },
                  });
                  editStepModal.close();
                  setSelectedStepId(null);
                }}
                submitting={updateFlowStep.isPending}
              />
            </div>
          </Modal>
        )}

        {/* Step Type Management Modal */}
        <FlowStepTypeManagementModal
          open={stepTypeManagementModal.isOpen}
          onOpenChange={stepTypeManagementModal.toggle}
        />

        {/* Instructions Modal */}
        <Modal
          open={instructionsModal.isOpen}
          onOpenChange={instructionsModal.toggle}
          title="ຄຳແນະນຳການໃຊ້ງານ Flow Editor"
          size="lg"
        >
          <div className="max-h-[calc(100vh-12rem)] space-y-4 overflow-y-auto">
            <div>
              <p className="mb-2 font-semibold text-sm">
                ການຈັດການຂັ້ນຕອນ (Flow Steps):
              </p>
              <ul className="ml-2 list-inside list-disc space-y-1.5 text-muted-foreground text-sm">
                <li>ກົດປຸ່ມ "ເພີ່ມຂັ້ນຕອນ" ເພື່ອສ້າງຂັ້ນຕອນໃໝ່</li>
                <li>ລາກຂັ້ນຕອນເພື່ອຍ້າຍຕຳແໜ່ງໃນຜະແນກຜັງ</li>
                <li>ກົດທີ່ຂັ້ນຕອນເພື່ອເລືອກ (ຈະເຫັນລາຍລະອຽດໃນດ້ານລຸ່ມ)</li>
                <li>ກົດສອງຄັ້ງທີ່ຂັ້ນຕອນເພື່ອເປີດແກ້ໄຂ</li>
                <li>ກົດສອງຄັ້ງທີ່ລາຍການຂັ້ນຕອນໃນ sidebar ເພື່ອແກ້ໄຂ</li>
                <li>ເລືອກຂັ້ນຕອນແລ້ວກົດ "ລຶບຂັ້ນຕອນ" ເພື່ອລຶບ</li>
              </ul>
            </div>
            <div>
              <p className="mb-2 font-semibold text-sm">
                ການຈັດການການເຊື່ອມຕໍ່ (Connections):
              </p>
              <ul className="ml-2 list-inside list-disc space-y-1.5 text-muted-foreground text-sm">
                <li>ກົດປຸ່ມ "ເພີ່ມການເຊື່ອມຕໍ່" ເພື່ອສ້າງການເຊື່ອມຕໍ່ໃໝ່</li>
                <li>ເລືອກຂັ້ນຕອນເລີ່ມຕົ້ນ (From) ແລະຂັ້ນຕອນປາຍທາງ (To)</li>
                <li>
                  ການເຊື່ອມຕໍ່ຈະຖືກສ້າງອັດຕະໂນມັດຕາມຕຳແໜ່ງຂອງຂັ້ນຕອນ (ແນວຕັ້ງ ຫຼື ແນວນອນ)
                </li>
                <li>ກົດສອງຄັ້ງທີ່ການເຊື່ອມຕໍ່ໃນຜະແນກຜັງເພື່ອແກ້ໄຂ ຫຼື ລຶບ</li>
                <li>ສາມາດເພີ່ມຊື່ການເຊື່ອມຕໍ່ໄດ້ (ບໍ່ບັງຄັບ)</li>
              </ul>
            </div>
            <div>
              <p className="mb-2 font-semibold text-sm">ເຄື່ອງມືອື່ນໆ:</p>
              <ul className="ml-2 list-inside list-disc space-y-1.5 text-muted-foreground text-sm">
                <li>ປຸ່ມ "ຍົກເລີກ" (Undo) ແລະ "ເຮັດຊ້ຳ" (Redo) ເພື່ອກັບຄືນການປ່ຽນແປງ</li>
                <li>ຕຳແໜ່ງຂອງຂັ້ນຕອນຈະຖືກບັນທຶກອັດຕະໂນມັດເມື່ອລາກ</li>
              </ul>
            </div>
          </div>
        </Modal>

        {/* Create Connection Modal */}
        <Modal
          open={createConnectionModal.isOpen}
          onOpenChange={(open) => {
            if (!open) {
              createConnectionModal.close();
              setConnectionForm({ fromStepId: "", toStepId: "", label: "" });
            }
          }}
          title="ເພີ່ມການເຊື່ອມຕໍ່"
          size="md"
        >
          <div className="space-y-4">
            <div className="space-y-2">
              <span className="font-medium text-sm">
                ຈາກຂັ້ນຕອນ <span className="text-destructive">*</span>
              </span>
              <SimpleSelect
                value={connectionForm.fromStepId}
                onValueChange={(value: string) =>
                  setConnectionForm((prev) => ({ ...prev, fromStepId: value }))
                }
                options={
                  flow.data?.steps
                    ?.filter(
                      (step: { id: string }) =>
                        step.id !== connectionForm.toStepId,
                    )
                    .map(
                      (step: {
                        id: string;
                        stepNumber: number;
                        title: string;
                      }) => ({
                        value: step.id,
                        label: `${step.stepNumber}. ${step.title}`,
                      }),
                    ) || []
                }
                placeholder="ເລືອກຂັ້ນຕອນເລີ່ມຕົ້ນ"
              />
            </div>
            <div className="space-y-2">
              <span className="font-medium text-sm">
                ໄປຫາຂັ້ນຕອນ <span className="text-destructive">*</span>
              </span>
              <SimpleSelect
                value={connectionForm.toStepId}
                onValueChange={(value: string) =>
                  setConnectionForm((prev) => ({ ...prev, toStepId: value }))
                }
                options={
                  flow.data?.steps
                    ?.filter(
                      (step: { id: string }) =>
                        step.id !== connectionForm.fromStepId,
                    )
                    .map(
                      (step: {
                        id: string;
                        stepNumber: number;
                        title: string;
                      }) => ({
                        value: step.id,
                        label: `${step.stepNumber}. ${step.title}`,
                      }),
                    ) || []
                }
                placeholder="ເລືອກຂັ້ນຕອນປາຍທາງ"
              />
            </div>
            <div className="space-y-2">
              <span className="font-medium text-sm">ຊື່ການເຊື່ອມຕໍ່ (ບໍ່ບັງຄັບ)</span>
              <Input
                value={connectionForm.label}
                onChange={(e) =>
                  setConnectionForm((prev) => ({
                    ...prev,
                    label: e.target.value,
                  }))
                }
                placeholder="ຕົວຢ່າງ: ຜ່ານ, ບໍ່ຜ່ານ, ຕ້ອງແກ້ໄຂ"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  createConnectionModal.close();
                  setConnectionForm({
                    fromStepId: "",
                    toStepId: "",
                    label: "",
                  });
                }}
              >
                ຍົກເລີກ
              </Button>
              <Button
                onClick={async () => {
                  if (!connectionForm.fromStepId || !connectionForm.toStepId) {
                    return;
                  }

                  // Check if connection already exists
                  const exists = edges.find(
                    (e) =>
                      e.source === connectionForm.fromStepId &&
                      e.target === connectionForm.toStepId,
                  );
                  if (exists) {
                    await confirm({
                      title: "ການເຊື່ອມຕໍ່ນີ້ມີຢູ່ແລ້ວ",
                      description: "ກະລຸນາເລືອກຂັ້ນຕອນອື່ນ",
                      actionText: "ຕົກລົງ",
                    });
                    return;
                  }

                  // Get node positions to determine best handle positions
                  const sourceNode = nodes.find(
                    (n) => n.id === connectionForm.fromStepId,
                  );
                  const targetNode = nodes.find(
                    (n) => n.id === connectionForm.toStepId,
                  );
                  const handles = getHandlePositions(sourceNode, targetNode);

                  const tempId = `temp-${Date.now()}`;
                  const newEdge = {
                    id: tempId,
                    source: connectionForm.fromStepId,
                    target: connectionForm.toStepId,
                    sourceHandle: handles.sourceHandle,
                    targetHandle: handles.targetHandle,
                    label: connectionForm.label || undefined,
                    type: "smoothstep" as const,
                    style: {
                      stroke: "#3b82f6",
                      strokeWidth: 3,
                    },
                  };
                  setEdges([...edges, newEdge]);

                  createConnectionModal.close();
                  const formData = { ...connectionForm };
                  setConnectionForm({
                    fromStepId: "",
                    toStepId: "",
                    label: "",
                  });

                  // Save connection to backend
                  createFlowConnection.mutate(
                    {
                      fromStepId: formData.fromStepId,
                      toStepId: formData.toStepId,
                      label: formData.label || undefined,
                    },
                    {
                      onSuccess: (connection) => {
                        // Update edge with real ID from backend
                        setEdges((prevEdges) =>
                          prevEdges.map((e) =>
                            e.id === tempId
                              ? {
                                  ...e,
                                  id: connection.id,
                                  style: {
                                    stroke: "#3b82f6",
                                    strokeWidth: 3,
                                  },
                                }
                              : e,
                          ),
                        );
                      },
                      onError: () => {
                        // Remove edge if creation failed
                        setEdges((prevEdges) =>
                          prevEdges.filter((e) => e.id !== tempId),
                        );
                      },
                    },
                  );
                }}
                disabled={
                  !connectionForm.fromStepId ||
                  !connectionForm.toStepId ||
                  createFlowConnection.isPending
                }
              >
                {createFlowConnection.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Edit Connection Modal */}
        {editConnectionModal.data && (
          <Modal
            open={editConnectionModal.isOpen}
            onOpenChange={editConnectionModal.toggle}
            title="ແກ້ໄຂການເຊື່ອມຕໍ່"
            size="md"
          >
            <div className="space-y-4">
              <div className="space-y-2">
                <span className="font-medium text-sm">
                  ຈາກຂັ້ນຕອນ <span className="text-destructive">*</span>
                </span>
                <SimpleSelect
                  value={editConnectionModal.data.fromStepId}
                  onValueChange={(value: string) => {
                    if (!editConnectionModal.data) return;
                    editConnectionModal.openWith({
                      ...editConnectionModal.data,
                      fromStepId: value,
                    });
                  }}
                  options={
                    flow.data?.steps
                      ?.filter(
                        (step: { id: string }) =>
                          step.id !== editConnectionModal.data!.toStepId,
                      )
                      .map(
                        (step: {
                          id: string;
                          stepNumber: number;
                          title: string;
                        }) => ({
                          value: step.id,
                          label: `${step.stepNumber}. ${step.title}`,
                        }),
                      ) || []
                  }
                  placeholder="ເລືອກຂັ້ນຕອນເລີ່ມຕົ້ນ"
                />
              </div>
              <div className="space-y-2">
                <span className="font-medium text-sm">
                  ໄປຫາຂັ້ນຕອນ <span className="text-destructive">*</span>
                </span>
                <SimpleSelect
                  value={editConnectionModal.data.toStepId}
                  onValueChange={(value: string) => {
                    if (!editConnectionModal.data) return;
                    editConnectionModal.openWith({
                      ...editConnectionModal.data,
                      toStepId: value,
                    });
                  }}
                  options={
                    flow.data?.steps
                      ?.filter(
                        (step: { id: string }) =>
                          step.id !== editConnectionModal.data!.fromStepId,
                      )
                      .map(
                        (step: {
                          id: string;
                          stepNumber: number;
                          title: string;
                        }) => ({
                          value: step.id,
                          label: `${step.stepNumber}. ${step.title}`,
                        }),
                      ) || []
                  }
                  placeholder="ເລືອກຂັ້ນຕອນປາຍທາງ"
                />
              </div>
              <div className="space-y-2">
                <span className="font-medium text-sm">ຊື່ການເຊື່ອມຕໍ່ (ບໍ່ບັງຄັບ)</span>
                <Input
                  value={editConnectionModal.data.label || ""}
                  onChange={(e) => {
                    editConnectionModal.openWith({
                      ...editConnectionModal.data!,
                      label: e.target.value || null,
                    });
                  }}
                  placeholder="ຕົວຢ່າງ: ຜ່ານ, ບໍ່ຜ່ານ, ຕ້ອງແກ້ໄຂ"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    editConnectionModal.close();
                    setSelectedConnectionId(null);
                  }}
                >
                  ຍົກເລີກ
                </Button>
                <Button
                  variant="destructive"
                  onClick={async () => {
                    if (
                      await confirm({
                        title: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບການເຊື່ອມຕໍ່ນີ້?",
                        description: "ການເຊື່ອມຕໍ່ນີ້ຈະຖືກລຶບອອກຈາກ Flow",
                        actionText: "ລຶບ",
                        cancelText: "ຍົກເລີກ",
                        ActionProps: {
                          variant: "destructive",
                        },
                      })
                    ) {
                      await deleteFlowConnection.mutateAsync(
                        editConnectionModal.data!.id,
                      );
                      setEdges((prevEdges) =>
                        prevEdges.filter(
                          (e) => e.id !== editConnectionModal.data!.id,
                        ),
                      );
                      editConnectionModal.close();
                      setSelectedConnectionId(null);
                    }
                  }}
                >
                  ລຶບ
                </Button>
                <Button
                  onClick={async () => {
                    if (
                      !editConnectionModal.data!.fromStepId ||
                      !editConnectionModal.data!.toStepId
                    ) {
                      return;
                    }

                    // Check if connection already exists (excluding current)
                    const exists = edges.find(
                      (e) =>
                        e.id !== editConnectionModal.data!.id &&
                        e.source === editConnectionModal.data!.fromStepId &&
                        e.target === editConnectionModal.data!.toStepId,
                    );
                    if (exists) {
                      await confirm({
                        title: "ການເຊື່ອມຕໍ່ນີ້ມີຢູ່ແລ້ວ",
                        description: "ກະລຸນາເລືອກຂັ້ນຕອນອື່ນ",
                        actionText: "ຕົກລົງ",
                      });
                      return;
                    }

                    // Get node positions to determine best handle positions
                    const sourceNode = nodes.find(
                      (n) => n.id === editConnectionModal.data!.fromStepId,
                    );
                    const targetNode = nodes.find(
                      (n) => n.id === editConnectionModal.data!.toStepId,
                    );
                    const handles = getHandlePositions(sourceNode, targetNode);

                    await updateFlowConnection.mutateAsync({
                      connectionId: editConnectionModal.data!.id,
                      data: {
                        fromStepId: editConnectionModal.data!.fromStepId,
                        toStepId: editConnectionModal.data!.toStepId,
                        label: editConnectionModal.data!.label || null,
                      },
                    });

                    // Update edge in state with new handle positions
                    setEdges((prevEdges) =>
                      prevEdges.map((e) =>
                        e.id === editConnectionModal.data!.id
                          ? {
                              ...e,
                              source: editConnectionModal.data!.fromStepId,
                              target: editConnectionModal.data!.toStepId,
                              sourceHandle: handles.sourceHandle,
                              targetHandle: handles.targetHandle,
                              label:
                                editConnectionModal.data!.label || undefined,
                            }
                          : e,
                      ),
                    );
                    editConnectionModal.close();
                    setSelectedConnectionId(null);
                  }}
                  disabled={
                    !editConnectionModal.data!.fromStepId ||
                    !editConnectionModal.data!.toStepId ||
                    updateFlowConnection.isPending
                  }
                >
                  {updateFlowConnection.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </Main>
    </>
  );
}
